import React, { useState, useEffect, useMemo, useCallback } from 'react';
import IntelTickerBar from './IntelTickerBar';
import PortfolioHedgePanel from './PortfolioHedgePanel';
import ResearchFundamentalsTable from '../../Research/ResearchFundamentalsTable';
import ResearchFinancialsTable from '../../Research/ResearchFinancialsTable';
import ResearchEPSTrendsTable from '../../Research/ResearchEPSTrendsTable';
import FairValueTable from '../../StockAnalyzer/FairValueTable';
import EarningsAnalysisTable from '../../StockAnalyzer/EarningsAnalysisTable';
import PriceChartModal from '../../Research/PriceChartModal';
import {
  runResearchFundamentals,
  runResearchFinancials,
  runResearchEpsTrends,
  runFairValue,
  runEarnings,
  getPortfolioPeerSets,
  savePortfolioPeerSet,
} from '../../../services/api';
import {
  uniqueTickers,
  INTEL_COMPARE_MODES,
  MAX_COMPARE_PEERS,
  PORTFOLIO_INTEL_TICKER_KEY,
  PORTFOLIO_INTEL_TAB_KEY,
  PORTFOLIO_INTEL_COMPARE_MODE_KEY,
  PORTFOLIO_INTEL_HOLDING_PEERS_KEY,
} from '../portfolioConstants';

/**
 * Stable empty array for the "compare against nothing" case. A fresh [] each
 * render would give `peers` a new identity every time and re-run every memo
 * chained off it, including the one that keys the result cache.
 */
const NO_PEERS = [];

const TABS = [
  { key: 'fundamentals', label: 'Fundamentals' },
  { key: 'financials', label: 'Financials' },
  { key: 'eps', label: 'EPS Trends' },
  { key: 'fairValue', label: 'Fair Value' },
  { key: 'earnings', label: 'Earnings' },
  { key: 'hedge', label: 'Hedge / Protect' },
];

/**
 * Fetched results, surviving unmount.
 *
 * CollapsibleSection unmounts its children, so without this every collapse and
 * re-expand would throw away the tables and refetch. These endpoints don't
 * cache and pace themselves with server-side sleeps, so that's a 5-10s round
 * trip to redraw data that was on screen a second ago.
 *
 * Deliberately an in-memory Map rather than sessionStorage: fundamentals move
 * intraday, and a hard refresh should get fresh numbers.
 */
const resultCache = new Map();

/**
 * Financials is the one tab whose payload depends on more than the ticker set —
 * the 5yr/10yr toggle changes it. Without the year in the key, re-expanding the
 * section could restore 10yr data while the (remounted) toggle reads 5yr.
 */
const cacheKey = (tab, scope, years) =>
  tab === 'financials' ? `financials:${scope}:${years}` : `${tab}:${scope}`;

/**
 * Test-only. The cache is module-level so it survives the unmount that
 * CollapsibleSection performs on collapse — which also means it would leak
 * between specs. Jest can't isolate it via resetModules (that gives the
 * component a second React instance and the hooks blow up), so specs clear it
 * through here instead.
 */
export const __clearIntelCacheForTests = () => resultCache.clear();

const readStored = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
};

/**
 * The holdings picker persists a list rather than a scalar. Returns null — not
 * [] — when nothing is stored, because "never chosen" (seed from holdings) and
 * "chosen nothing" (compare against none of them) are different states.
 */
const readStoredList = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : null;
  } catch {
    return null;
  }
};

/**
 * Company Intel — the research layer for what you already own.
 *
 * Two deliberate differences from the Research / Stock Analyzer tools:
 *
 *   1. No Analysis Input step. Tickers come from your holdings.
 *   2. One holding at a time, plus a SAVED peer set. This isn't only a UX
 *      preference — none of these endpoints cache and they pace themselves with
 *      hardcoded sleeps (fundamentals and financials each cost ~10 provider
 *      calls and ~2.5s per ticker), so analysing every holding at once would
 *      blow the request timeout. One ticker plus a few peers stays fast.
 *
 * Every table below is the existing component, imported unchanged. Nothing in
 * research/ or stock_analyzer/ is modified.
 */
export default function PortfolioIntel({ contracts = [] }) {
  const tickers = useMemo(() => uniqueTickers(contracts), [contracts]);

  const [selectedTicker, setSelectedTicker] = useState(() =>
    readStored(PORTFOLIO_INTEL_TICKER_KEY, '')
  );
  const [activeTab, setActiveTab] = useState(() =>
    readStored(PORTFOLIO_INTEL_TAB_KEY, 'fundamentals')
  );

  const [compareMode, setCompareMode] = useState(() =>
    readStored(PORTFOLIO_INTEL_COMPARE_MODE_KEY, INTEL_COMPARE_MODES.COMPETITORS)
  );
  const [holdingSelection, setHoldingSelection] = useState(() =>
    readStoredList(PORTFOLIO_INTEL_HOLDING_PEERS_KEY)
  );

  const [peerSets, setPeerSets] = useState({});
  const [peerSetsLoaded, setPeerSetsLoaded] = useState(false);

  const [financialsYears, setFinancialsYears] = useState(5);
  const [chartTicker, setChartTicker] = useState(null);

  // Results and in-flight state, keyed by tab. Rehydrated from the module cache
  // by the fetch effect below, which knows the exact scope key to look up.
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  // Keep the selection valid as holdings change (position added, sold, etc.).
  useEffect(() => {
    if (tickers.length === 0) return;
    if (!selectedTicker || !tickers.includes(selectedTicker)) {
      setSelectedTicker(tickers[0]);
    }
  }, [tickers, selectedTicker]);

  useEffect(() => {
    if (selectedTicker) writeStored(PORTFOLIO_INTEL_TICKER_KEY, selectedTicker);
  }, [selectedTicker]);

  useEffect(() => {
    writeStored(PORTFOLIO_INTEL_TAB_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    writeStored(PORTFOLIO_INTEL_COMPARE_MODE_KEY, compareMode);
  }, [compareMode]);

  // Seed the picker with every holding the first time it is needed, so turning
  // on Holdings mode compares against the whole portfolio rather than nothing.
  useEffect(() => {
    if (holdingSelection === null && tickers.length > 0) setHoldingSelection(tickers);
  }, [tickers, holdingSelection]);

  useEffect(() => {
    if (holdingSelection === null) return;
    writeStored(PORTFOLIO_INTEL_HOLDING_PEERS_KEY, JSON.stringify(holdingSelection));
  }, [holdingSelection]);

  // One call for every peer set, on first mount of the (expanded) section.
  useEffect(() => {
    let cancelled = false;
    getPortfolioPeerSets()
      .then((res) => {
        if (!cancelled) setPeerSets(res.data || {});
      })
      .catch((err) => console.error('Failed to load peer sets:', err))
      .finally(() => {
        if (!cancelled) setPeerSetsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const savedPeers = useMemo(
    () => peerSets[selectedTicker] || NO_PEERS,
    [peerSets, selectedTicker]
  );

  // Other positions available to compare against — the pills Holdings mode shows.
  const holdingOptions = useMemo(
    () => tickers.filter((t) => t !== selectedTicker),
    [tickers, selectedTicker]
  );

  // The `tickers.includes` filter is load-bearing: a persisted selection can
  // name a position that has since been sold, and a stale ticker would
  // otherwise be analysed silently.
  const holdingPeers = useMemo(() => {
    const chosen = holdingSelection || tickers;
    return chosen
      .filter((t) => t !== selectedTicker && tickers.includes(t))
      .slice(0, MAX_COMPARE_PEERS);
  }, [holdingSelection, tickers, selectedTicker]);

  // The one value the whole section keys off. Everything downstream —
  // effectiveTickers, scopeKey, the result cache, the fetch effect — already
  // reacts to this, so choosing a mode needs no other wiring. Flipping between
  // modes even restores from resultCache without a round trip.
  const peers = useMemo(() => {
    if (compareMode === INTEL_COMPARE_MODES.NONE) return NO_PEERS;
    if (compareMode === INTEL_COMPARE_MODES.HOLDINGS) return holdingPeers;
    return savedPeers;
  }, [compareMode, holdingPeers, savedPeers]);

  const toggleHolding = useCallback((ticker) => {
    setHoldingSelection((prev) => {
      const current = prev || [];
      if (current.includes(ticker)) return current.filter((t) => t !== ticker);
      return [...current, ticker];
    });
  }, []);

  // What actually gets analysed: the holding, then its saved comparisons.
  const effectiveTickers = useMemo(
    () => (selectedTicker ? [selectedTicker, ...peers] : []),
    [selectedTicker, peers]
  );

  // Cache key — results are only valid for one ticker+peer combination.
  const scopeKey = useMemo(() => effectiveTickers.join(','), [effectiveTickers]);

  const handlePeersChange = async (nextPeers) => {
    // Rolls back the SAVED set specifically. In Competitors mode this is the
    // same list as `peers`, but the editor only ever edits the saved one.
    const previous = savedPeers;
    setPeerSets((prev) => ({ ...prev, [selectedTicker]: nextPeers }));
    try {
      await savePortfolioPeerSet(selectedTicker, nextPeers);
    } catch (err) {
      console.error('Failed to save peer set:', err);
      setPeerSets((prev) => ({ ...prev, [selectedTicker]: previous }));
    }
  };

  const fetchTab = useCallback(
    async (tab, tickerList, years) => {
      if (tab === 'hedge' || tickerList.length === 0) return;

      setLoading((prev) => ({ ...prev, [tab]: true }));
      setErrors((prev) => ({ ...prev, [tab]: '' }));

      try {
        let payload;
        switch (tab) {
          case 'fundamentals':
            payload = (await runResearchFundamentals(tickerList)).data.research_fundamentals || [];
            break;
          case 'financials':
            payload = (await runResearchFinancials(tickerList, years)).data;
            break;
          case 'eps':
            payload = (await runResearchEpsTrends(tickerList)).data.eps_trends || [];
            break;
          case 'fairValue':
            payload = (await runFairValue(tickerList)).data.fair_value_results || [];
            break;
          case 'earnings':
            payload = (await runEarnings(tickerList)).data.earnings_results || [];
            break;
          default:
            return;
        }
        const key = tickerList.join(',');
        resultCache.set(cacheKey(tab, key, years), payload);
        setResults((prev) => ({ ...prev, [tab]: { scopeKey: key, years, payload } }));
      } catch (err) {
        console.error(`Intel ${tab} fetch failed:`, err);
        setErrors((prev) => ({
          ...prev,
          [tab]: err.response?.data?.error || 'Could not load this data.',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, [tab]: false }));
      }
    },
    []
  );

  // Fetch on demand: only the visible tab, and only when its cached result
  // doesn't match the current ticker + peer scope.
  useEffect(() => {
    if (!peerSetsLoaded || effectiveTickers.length === 0) return;
    if (activeTab === 'hedge') return;
    if (loading[activeTab]) return;

    const current = results[activeTab];
    const isCurrent =
      current?.scopeKey === scopeKey &&
      (activeTab !== 'financials' || current?.years === financialsYears);
    if (isCurrent) return;

    // Survives a collapse/expand cycle: if this exact tab+scope was already
    // fetched, restore it instead of paying for the round trip again.
    const cached = resultCache.get(cacheKey(activeTab, scopeKey, financialsYears));
    if (cached !== undefined) {
      setResults((prev) => ({
        ...prev,
        [activeTab]: { scopeKey, years: financialsYears, payload: cached },
      }));
      return;
    }

    fetchTab(activeTab, effectiveTickers, financialsYears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scopeKey, peerSetsLoaded, financialsYears]);

  // Just move the state — the fetch effect watches financialsYears and will
  // either restore the cached 5yr/10yr payload or fetch it. Calling fetchTab
  // here too would mean two paths racing to load the same tab.
  const handleYearsChange = (years) => setFinancialsYears(years);

  const tabData = results[activeTab]?.payload;
  const isLoading = !!loading[activeTab];
  const tabError = errors[activeTab];

  const selectedContracts = useMemo(
    () => contracts.filter((c) => String(c.ticker).toUpperCase() === selectedTicker),
    [contracts, selectedTicker]
  );

  const renderTab = () => {
    if (activeTab === 'hedge') {
      return <PortfolioHedgePanel ticker={selectedTicker} contracts={selectedContracts} />;
    }

    if (isLoading && !tabData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3" />
          <span className="text-sm text-muted">
            Loading {effectiveTickers.join(', ')}…
          </span>
        </div>
      );
    }

    if (tabError && !tabData) {
      return <p className="text-red-400 text-sm py-8 text-center">{tabError}</p>;
    }

    switch (activeTab) {
      case 'fundamentals':
        return <ResearchFundamentalsTable data={tabData || []} onTickerClick={setChartTicker} />;
      case 'financials':
        return (
          <ResearchFinancialsTable
            data={tabData}
            years={financialsYears}
            onYearsChange={handleYearsChange}
            onTickerClick={setChartTicker}
          />
        );
      case 'eps':
        return <ResearchEPSTrendsTable data={tabData || []} onTickerClick={setChartTicker} />;
      case 'fairValue':
        return <FairValueTable data={tabData || []} />;
      case 'earnings':
        return <EarningsAnalysisTable data={tabData || []} />;
      default:
        return null;
    }
  };

  if (tickers.length === 0) {
    return (
      <p className="text-sm text-muted italic">
        Add a holding to your portfolio and its company data will show up here.
      </p>
    );
  }

  return (
    <div>
      <IntelTickerBar
        tickers={tickers}
        selectedTicker={selectedTicker}
        onSelectTicker={setSelectedTicker}
        peers={savedPeers}
        onPeersChange={handlePeersChange}
        compareMode={compareMode}
        onCompareModeChange={setCompareMode}
        holdingOptions={holdingOptions}
        selectedHoldings={holdingPeers}
        onToggleHolding={toggleHolding}
        // Peers never reach PortfolioHedgePanel, so offering the control on
        // that tab would imply a setting that does nothing.
        showCompare={activeTab !== 'hedge'}
        disabled={isLoading}
      />

      <div
        className="flex flex-wrap gap-2 mb-4 pb-3"
        style={{ borderBottom: '1px solid var(--pf-border)' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`tab-button ${activeTab === tab.key ? 'tab-selected' : 'tab-unselected'}`}
          >
            {tab.label}
            {loading[tab.key] && ' …'}
          </button>
        ))}
      </div>

      {/* Refreshing an already-populated tab shouldn't blank it out — keep the
          stale table visible and just dim it. */}
      <div style={{ opacity: isLoading && tabData ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {renderTab()}
      </div>

      <PriceChartModal
        isOpen={!!chartTicker}
        onClose={() => setChartTicker(null)}
        ticker={chartTicker}
      />
    </div>
  );
}
